import { cn } from "@/lib/utils";
import { getConsultantName } from "./scheduleView/week/ApplicantList";
import { getInitials } from "./scheduleView/week/weekSchedule.utils";
import HoverPopover from "./HoverPopover";

/** Lines of the presentation shown before it is cut off */
const PRESENTATION_LINES = 3;

export interface ConsultantPopoverData {
	firstName?: string;
	lastName?: string;
	profile?: { profilePicture?: string; presentation?: string; city?: string };
}

interface Props {
	consultant: ConsultantPopoverData;
	/** Shows a "Bokad" tag next to the name */
	booked?: boolean;
	/** Classes for the hovered wrapper around `children` */
	anchorClassName?: string;
	children: React.ReactNode;
}

/** Shows a larger picture, name, home town and the start of the consultant's presentation while `children` is hovered */
const ConsultantPopover = ({ consultant, booked, anchorClassName = "shrink-0", children }: Props) => {
	const { profilePicture, presentation, city } = consultant.profile || {};
	return (
		<HoverPopover
			anchorClassName={anchorClassName}
			className="w-72"
			content={
				<div className="flex items-start gap-3">
					{profilePicture ? (
						<img src={profilePicture} alt="" className="!h-14 !w-14 shrink-0 rounded-full object-cover" />
					) : (
						<span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gray-200 text-base font-bold text-gray-700">
							{getInitials(consultant)}
						</span>
					)}
					<div className="min-w-0">
						<div className="flex items-center gap-2">
							<span className="truncate text-sm font-semibold text-gray-900">{getConsultantName(consultant)}</span>
							{booked && (
								<span className="shrink-0 rounded-sm bg-[#49BD79] px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white">
									Bokad
								</span>
							)}
						</div>
						{city && <div className="truncate text-xs text-gray-500">{city}</div>}
						<div
							className={cn("mt-1 whitespace-pre-wrap text-sm text-gray-700", !presentation && "italic text-gray-500")}
							style={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: PRESENTATION_LINES, overflow: "hidden" }}
						>
							{presentation || "Ingen presentation"}
						</div>
					</div>
				</div>
			}
		>
			{children}
		</HoverPopover>
	);
};

export default ConsultantPopover;
